import type { RecurrenceFormValues } from '@clarix/utils';
import { format } from 'date-fns';

// ── Duration Presets ──────────────────────────────────────────────────

export interface DurationPreset {
  label: string;
  minutes: number;
}

export const DURATION_PRESETS: DurationPreset[] = [
  { label: '15m', minutes: 15 },
  { label: '30m', minutes: 30 },
  { label: '45m', minutes: 45 },
  { label: '1h', minutes: 60 },
  { label: '1.5h', minutes: 90 },
  { label: '2h', minutes: 120 },
];

// ── Recurrence Presets ────────────────────────────────────────────────

export type RecurrencePresetValue =
  | 'none'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'custom';

export interface RecurrencePreset {
  label: string;
  value: RecurrencePresetValue;
}

export const RECURRENCE_PRESETS: RecurrencePreset[] = [
  { label: 'Does not repeat', value: 'none' },
  { label: 'Daily', value: 'daily' },
  { label: 'Every weekday (Mon–Fri)', value: 'weekdays' },
  { label: 'Weekly on {day}', value: 'weekly' },
  { label: 'Bi-weekly on {day}', value: 'biweekly' },
  { label: 'Monthly on the {date}', value: 'monthly' },
  { label: 'Custom...', value: 'custom' },
];

// ── People Picker ─────────────────────────────────────────────────────

export interface PeoplePickerPerson {
  id: string;
  name: string;
  email: string;
  role?: string;
  type: 'team' | 'org' | 'external';
}

// ── Helpers ───────────────────────────────────────────────────────────

/** Build a human-readable label for the current recurrence value */
export function buildRecurrenceLabel(
  value: RecurrenceFormValues | undefined,
  startDate: Date | null,
): string {
  if (!value || value.recurrenceType === 'none') return 'Does not repeat';

  const dayName = startDate ? format(startDate, 'EEEE') : 'this day';
  const dateNum = startDate ? getOrdinal(startDate.getDate()) : '';

  const { recurrenceType, interval, daysOfWeek } = value;

  // Detect preset matches
  if (recurrenceType === 'daily' && interval === 1) return 'Daily';

  if (recurrenceType === 'weekly') {
    const weekdays = [0, 1, 2, 3, 4]; // Mon–Fri
    const isWeekdays =
      interval === 1 &&
      daysOfWeek?.length === 5 &&
      weekdays.every((d) => daysOfWeek.includes(d));
    if (isWeekdays) return 'Every weekday (Mon–Fri)';

    if (interval === 1) return `Weekly on ${dayName}`;
    if (interval === 2) return `Bi-weekly on ${dayName}`;
  }

  if (recurrenceType === 'monthly' && interval === 1) {
    return `Monthly on the ${dateNum}`;
  }

  // Custom
  const unitLabel =
    recurrenceType === 'daily'
      ? 'day'
      : recurrenceType === 'weekly'
        ? 'week'
        : 'month';
  return `Every ${interval > 1 ? `${interval} ` : ''}${unitLabel}${interval > 1 ? 's' : ''}`;
}

/** Map a recurrence preset to RecurrenceFormValues */
export function presetToRecurrenceValues(
  preset: RecurrencePresetValue,
  startDate: Date | null,
): RecurrenceFormValues {
  const base: RecurrenceFormValues = {
    recurrenceType: 'none',
    interval: 1,
    daysOfWeek: [],
    dayOfMonth: undefined,
    recurrenceEndType: undefined,
    recurrenceEndAfterCount: undefined,
    recurrenceEndDate: undefined,
  };

  switch (preset) {
    case 'none':
      return base;
    case 'daily':
      return { ...base, recurrenceType: 'daily' };
    case 'weekdays':
      return {
        ...base,
        recurrenceType: 'weekly',
        daysOfWeek: [0, 1, 2, 3, 4],
      };
    case 'weekly': {
      const dayIndex = startDate ? (startDate.getDay() + 6) % 7 : 0;
      return {
        ...base,
        recurrenceType: 'weekly',
        daysOfWeek: [dayIndex],
      };
    }
    case 'biweekly': {
      const dayIndex = startDate ? (startDate.getDay() + 6) % 7 : 0;
      return {
        ...base,
        recurrenceType: 'weekly',
        interval: 2,
        daysOfWeek: [dayIndex],
      };
    }
    case 'monthly':
      return {
        ...base,
        recurrenceType: 'monthly',
        dayOfMonth: startDate?.getDate() ?? 1,
      };
    default:
      return base;
  }
}

/** Get ordinal suffix for a number: 1st, 2nd, 3rd, etc. */
function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]!);
}
